import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/CompanyRegistrationProfileInfo';
import { CompanyUserAddCompanyRepository } from 'interfaceAdapter/repositories/CompanyUserAddCompany';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        area: yup.string().required(),
        number_of_employees: yup.string().required(),
        revenue_scale: yup.string().required(),
        profit_level: yup.string().required(),
        net_assets_level: yup.string().required(),
    })
    .required();

type AddCompanyForm = yup.InferType<typeof schema>;

export const useCompanyAddCompany = () => {
    const diContainer = useContext(diContainerContext);
    const companyRegistrationProfileInfoQuery = diContainer.resolve(CompanyRegistrationProfileInfoQuery);
    const addCompanyRepository = diContainer.resolve(CompanyUserAddCompanyRepository);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<AddCompanyForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    useEffect(() => {
        const fetchData = async () => {
            const data = await companyRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setProfitLevelChoices(data.profit_level_choices);
            setNetAssetsLevelChoices(data.net_assets_level_choices);
        };
        fetchData();
    }, []);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await addCompanyRepository.addCompany(data);
            setShowSnackbar(true);
            // current_company は backend で新会社に切り替わっているので、リロードで context 更新
            setTimeout(() => window.location.replace('/company/dashboard/'), 1500);
        } catch {
            setLoading(false);
            alert('企業の追加に失敗しました');
        }
    });

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        setShowSnackbar,
        submit,
    };
};
